export type MeasurementUnit = 'm' | 'cm';
export type RackRotation = 0 | 90 | 180 | 270;

export interface Warehouse {
  id: string;
  admin_id: string;
  name: string;
  unit: MeasurementUnit;
  length: number;
  width: number;
  height: number | null;
  border_color: string | null;
  map_unit: MeasurementUnit | null;
  grid_unit: MeasurementUnit | null;
  is_archived: boolean;
  archived_at: string | null;
  archived_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface WarehouseZone {
  id: string;
  warehouse_id: string;
  admin_id: string;
  zone_number: number;
  zone_code: string;
  zone_name: string;
  color: string;
  x_position: number;
  y_position: number;
  zone_width: number;
  zone_height: number;
  created_at: string;
  updated_at: string;
}

export interface WarehouseRack {
  id: string;
  warehouse_id: string;
  zone_id: string | null;
  admin_id: string;
  rack_number: number;
  rack_code: string;
  rack_name: string | null;
  rack_length: number;
  rack_depth: number;
  rack_height: number | null;
  rotation: RackRotation;
  levels: number;
  bays: number;
  x_position: number;
  y_position: number;
  zone_manual_override: boolean;
  created_at: string;
  updated_at: string;
}

export interface WarehouseBin {
  id: string;
  rack_id: string;
  warehouse_id: string;
  admin_id: string;
  bin_code: string;
  bay_letter: string;
  bay_index: number;
  level_number: number;
  sort_order: number;
  created_at: string;
}

export interface WarehouseLayout {
  id: string;
  warehouse_id: string;
  grid_size: number;
  viewport_json: ViewportState;
  updated_at: string;
}

export interface ViewportState {
  x: number;
  y: number;
  scale: number;
}

export interface WarehouseCreateData {
  name: string;
  unit: MeasurementUnit;
  length: number;
  width: number;
  height?: number | null;
}

export interface WarehouseUpdateData {
  name?: string;
  unit?: MeasurementUnit;
  length?: number;
  width?: number;
  height?: number | null;
  border_color?: string | null;
  map_unit?: MeasurementUnit | null;
  grid_unit?: MeasurementUnit | null;
}

export interface ZoneCreateData {
  warehouse_id: string;
  zone_name?: string;
  color: string;
  x_position?: number;
  y_position?: number;
  zone_width?: number;
  zone_height?: number;
}

export interface ZoneUpdateData {
  zone_name?: string;
  color?: string;
  x_position?: number;
  y_position?: number;
  zone_width?: number;
  zone_height?: number;
}

export interface RackCreateData {
  warehouse_id: string;
  zone_id?: string | null;
  rack_name?: string;
  rack_length?: number;
  rack_depth?: number;
  rack_height?: number | null;
  rotation?: RackRotation;
  levels?: number;
  bays?: number;
  x_position?: number;
  y_position?: number;
  zone_manual_override?: boolean;
}

export interface RackUpdateData {
  zone_id?: string | null;
  rack_name?: string;
  rack_length?: number;
  rack_depth?: number;
  rack_height?: number | null;
  rotation?: RackRotation;
  levels?: number;
  bays?: number;
  x_position?: number;
  y_position?: number;
  zone_manual_override?: boolean;
}

export interface WarehouseWithRelations extends Warehouse {
  zones?: WarehouseZone[];
  racks?: WarehouseRack[];
  layout?: WarehouseLayout | null;
}

export interface RackWithBins extends WarehouseRack {
  bins: WarehouseBin[];
  zone?: WarehouseZone | null;
}

export interface ZoneWithRacks extends WarehouseZone {
  racks: WarehouseRack[];
}

export interface WarehouseStatistics {
  total_zones: number;
  total_racks: number;
  total_bins: number;
  racks_in_zones: number;
  racks_unassigned: number;
  utilization_percentage: number;
}

export interface Position {
  x: number;
  y: number;
}

export interface Dimensions {
  width: number;
  height: number;
}

export interface BoundingBox extends Position, Dimensions {}

export interface ShapeTransform {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
}

export interface MapEditorState {
  selectedZoneId: string | null;
  selectedRackId: string | null;
  isDragging: boolean;
  isResizing: boolean;
  showGrid: boolean;
  snapToGrid: boolean;
  gridSize: number;
}

export type MapEditorTool = 'select' | 'pan' | 'zone' | 'rack';

export interface MapEditorConfig {
  activeTool: MapEditorTool;
  showZoneLabels: boolean;
  showRackLabels: boolean;
  showBinCounts: boolean;
  zoneOpacity: number;
}

export interface UnitConversionResult {
  value: number;
  unit: MeasurementUnit;
  displayValue: string;
}

export interface ValidationError {
  field: string;
  message: string;
}

export interface WarehouseValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

export const ZONE_COLORS = [
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
  '#06B6D4',
  '#84CC16',
  '#F97316',
  '#6366F1',
  '#14B8A6',
  '#A855F7',
] as const;

export const DEFAULT_ZONE_WIDTH = 200;
export const DEFAULT_ZONE_HEIGHT = 200;
export const DEFAULT_RACK_LENGTH = 100;
export const DEFAULT_RACK_DEPTH = 50;
export const DEFAULT_GRID_SIZE = 10;
export const MIN_GRID_SIZE = 5;
export const MAX_GRID_SIZE = 50;
export const MIN_ZOOM = 0.1;
export const MAX_ZOOM = 3;
export const ZOOM_STEP = 0.1;

export const WAREHOUSE_TABS = ['details', 'zones', 'racks', 'map'] as const;
export type WarehouseTab = typeof WAREHOUSE_TABS[number];

export type BinMovementType = 'assign' | 'transfer' | 'remove' | 'adjust';

export interface BinInventory {
  id: string;
  bin_id: string;
  product_id: string;
  warehouse_id: string;
  admin_id: string;
  quantity: number;
  assigned_by: string | null;
  assigned_at: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface BinInventoryWithProduct extends BinInventory {
  product: {
    id: string;
    name: string;
    sku: string;
    barcode: string | null;
    category: string;
    quantity: number;
    image_url: string | null;
  };
  assigned_by_user?: {
    id: string;
    full_name: string;
  } | null;
}

export interface BinMovementHistory {
  id: string;
  product_id: string;
  from_bin_id: string | null;
  to_bin_id: string | null;
  warehouse_id: string;
  admin_id: string;
  quantity: number;
  movement_type: BinMovementType;
  performed_by: string | null;
  reason: string | null;
  created_at: string;
}

export interface BinMovementHistoryWithDetails extends BinMovementHistory {
  product?: {
    id: string;
    name: string;
    sku: string;
  };
  from_bin?: {
    id: string;
    bin_code: string;
  } | null;
  to_bin?: {
    id: string;
    bin_code: string;
  } | null;
  performed_by_user?: {
    id: string;
    full_name: string;
  } | null;
}

export interface BinLocationAddress {
  warehouse_id: string;
  warehouse_name: string;
  zone_id: string | null;
  zone_code: string | null;
  rack_id: string;
  rack_code: string;
  bin_id: string;
  bin_code: string;
  full_address: string;
  short_code: string;
}

export interface ProductBinLocation extends BinLocationAddress {
  bin_inventory_id: string;
  quantity: number;
}

export interface BinContentsItem {
  bin_inventory_id: string;
  product_id: string;
  product_name: string;
  product_sku: string;
  product_barcode: string | null;
  product_category: string;
  quantity: number;
  total_product_quantity: number;
  assigned_by: string | null;
  assigned_by_name: string | null;
  assigned_at: string;
  notes: string | null;
  product_image_url: string | null;
}

export interface BinProductPreview {
  product_id: string;
  name: string;
  image_url: string | null;
}

export interface BinAssignmentData {
  bin_id: string;
  product_id: string;
  warehouse_id: string;
  quantity: number;
  notes?: string;
}

export interface BinTransferData {
  product_id: string;
  from_bin_id: string;
  to_bin_id: string;
  warehouse_id: string;
  quantity: number;
  reason?: string;
}

export interface WarehouseBinWithInventory extends WarehouseBin {
  inventory_count: number;
  inventory_items?: BinInventoryWithProduct[];
  preview_items?: BinProductPreview[];
}

export interface BinPickSuggestion {
  product_id: string;
  product_name: string;
  product_sku: string;
  requested_quantity: number;
  bin_location: BinLocationAddress;
  available_quantity: number;
  pick_order: number;
}
